import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { encryptStationPassword } from '../../utils/EncryptionUtils';
import { privilegedSkip } from '../../redux/actionCreators/processValidation';
import LargeButton from '../LargeButton/LargeButton';
import { Popover } from '@mui/material';
import styles from './SkipPart.module.scss';
import ReasonAndRemarkModal from '../ReportModal/ReasonAndRemarkModal';
import { useResolutionScale } from '../../hooks';
import ReLogin from '../ReLogin/ReLogin';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { isPuneJlr } from '../../constants';



const mapStateToProps = (state) => ({
	partValidation: state.partValidation,
	themeMode: state.theme.themeMode,
});

const mapDispatchToProps = (dispatch) => ({
	privilegedSkip: (asn, payload, successCallback, errorCallback) =>
		dispatch(privilegedSkip(asn, payload, successCallback, errorCallback)),
});



const skipButton = {
	background: '#064A88',
	'&:hover': { background: '#064A88', color: '#FBFBFB' },
	color: '#FBFBFB !important',
	fontWeight: 800,
	border: 'none',
};

const SkipPart = ({
	partValidation,

	currentPartNumber,
	selectedRowData,
	selectedTorqueRows = [], // Array of selected torque row IDs
	allTorqueRows = [], // All torque rows to find selected ones
	successCallback,
	errorCallback,
	btnStyles = { ...skipButton },
	skippedSerialNumber = '',
	skippedPartNumber = '',
	disableBtn = false,
	hoverMessage = '',
	skipModalTitle,
	setSecondApiStop,
	themeMode,
	privilegedSkip,
	torqueRows = [],
	skippart,
}) => {


	const { scaleValue } = useResolutionScale();
	const [isSkipDialogOpen, setIsSkipDialogOpen] = useState(false);
	const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
	const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
	const [isReLoginOpen, setIsReLoginOpen] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [loading, setLoading] = useState(false);
	const [skipReasonData, setSkipReasonData] = useState({ reason: '', remarks: '' });
	const { showMessageOverlay } = useMessageContextManager();

	const featuresRef = useRef([]);
	useEffect(() => {
		axiosInstance.get('/features').then(({ data }) => {
			featuresRef.current = data || [];
		}).catch(() => {});
	}, []);


	const handlePopoverOpen = (event) => {
		if (disableBtn) {
			setIsEllipsisPopupOpen(true);
			setEllipsisAnchorEl(event.currentTarget);
		}
	};
	const handlePopoverClose = () => {
		setIsEllipsisPopupOpen(false);
		setEllipsisAnchorEl(null);
	};

	const { scanJobDetails = [] } = partValidation;
	// console.log(skippedSerialNumber, "skippedSerialNumber");


	// Determine the target part data from props or current scan index
	const partData = (() => {
		if (skippart && typeof skippart === 'object' && Object.keys(skippart).length > 0) return skippart;
		if (selectedRowData && typeof selectedRowData === 'object' && Object.keys(selectedRowData).length > 0) return selectedRowData;
		if (typeof currentPartNumber === 'number' && scanJobDetails[currentPartNumber]) return scanJobDetails[currentPartNumber];
		return {};
	})();


	const { familyName, partNumber, quantity, scanJobResults = [] } = partData;

	// Helper to get family ID consistently from various object structures
	const getEffectiveFamilyId = (data) => {
		if (!data || typeof data !== 'object') return null;
		const idCandidate = data.familyId || data.id || data.partFamilyId;
		// Reject placeholder IDs generated by the UI
		if (!idCandidate || (typeof idCandidate === 'string' && idCandidate.startsWith('pv-'))) return null;
		return idCandidate;
	};


	const openSkipDialog = () => {
		if (partData?.genealogyRequired && partData?.partNumberScanningRequired) {
			showMessageOverlay({
				contextText: 'Skip Validation',
				info: 'Mandatory Part can not be Fail Forwarded',
				error: true,
			});
			return;
		}
		setIsSkipDialogOpen(true);
	};
	const closeSkipDialog = () => setIsSkipDialogOpen(false);


	const onSkipSubmit = (reason, remarks) => {
		setSkipReasonData({ reason, remarks });
		setIsSkipDialogOpen(false);
		setIsReLoginOpen(true);
	};

	const handleReLogin = async (e, username, password) => {
		e.preventDefault();
		// password is ALREADY encrypted by ReLogin component
		try {
			setLoading(true);

			const privilegedRequest = {
				username: username,
				password: password,
				action: 'GROUP_LEADER',
				remark: skipReasonData.remarks,
			};


			const isMachineOp = !!(partData && partData.machineId);

			// Mutually exclusive: If it's a machine operation, partSkipRequest must be null
			const validFamilyId = !isMachineOp ? getEffectiveFamilyId(partData) : null;

			const partSkipRequest = validFamilyId ? {
				familyId: validFamilyId,
				partNumber: partData.partNumber || partNumber || null,
				partPosition: partData.partPosition || null,
				scanJobResultId: partData.scanJobResultId || null,
				scannedSerialNumber: skippedSerialNumber || null,
				scannedPartNumber: skippedPartNumber || null,
				reason: skipReasonData.reason,
				remarks: skipReasonData.remarks,
			} : null;





			// Build parametersSkipRequest
			let parametersSkipRequest = null;

			// Handle multiple selected torque rows if provided
			const rowsToProcess = [];

			// If selectedTorqueRows array is provided with allTorqueRows, use that for multi-select
			if (selectedTorqueRows && selectedTorqueRows.length > 0 && allTorqueRows && allTorqueRows.length > 0) {
				rowsToProcess.push(...allTorqueRows.filter(row =>
					selectedTorqueRows.includes(row.id) || selectedTorqueRows.includes(String(row.id))
				));
			}
			// Otherwise, fall back to single row selection (backward compatibility)
			else if (partData && partData.machineId) {
				rowsToProcess.push(partData);
			}

			// Group processes to skip by machineId, handling multi-batch quantities
			if (rowsToProcess.length > 0) {
				// Read feature flag at handler call time (ref is always current, unlike a closure)
				const isDisplayOverallParamsFalse = featuresRef.current.find(
					(f) => f.name === 'DISPLAY_OVERALL_PARAMS'
				)?.enabled === false;

				const machineGroups = rowsToProcess.reduce((acc, row) => {
					if (!row.machineId) return acc;
					if (!acc[row.machineId]) {
						acc[row.machineId] = [];
					}

					const qty = row.quantity || 1;
					const executionStatuses = row.executionStatuses || Array.from({ length: qty }, () => null);
					const batchRawStatuses = row.batchRawStatuses || Array.from({ length: qty }, () => {
						if (isPuneJlr()) {
							const fallback = row.paramStatus || row.status;
							return (fallback && fallback.toUpperCase() !== 'PENDING') ? fallback : 'PENDING';
						}
						return 'PENDING';
					});
					// batchActualNumbers carries the real machine batchNumber for each index
					const batchActualNumbers = row.batchActualNumbers || Array.from({ length: qty }, (_, i) => i + 1);

					// Targeted skip: Skip only the remaining pending/NOK batches for the selected parameter
					for (let i = 0; i < qty; i++) {
						const isOk = executionStatuses[i] === true;
						if (!isOk) {
							let finalStatus = isDisplayOverallParamsFalse ? 'NOK' : batchRawStatuses[i];
							if (isPuneJlr()) {
								const currentStatus = batchRawStatuses[i];
								if (!currentStatus || currentStatus.toUpperCase() === 'PENDING') {
									const fallback = row.paramStatus || row.status;
									if (fallback && fallback.toUpperCase() !== 'PENDING') {
										finalStatus = fallback;
									} else {
										finalStatus = 'PENDING';
									}
								} else {
									finalStatus = currentStatus;
								}
							}
							acc[row.machineId].push({
								parameterName: row.parameterKey || row.parameterName || '',
								parameterDescription: row.parameterDescription || row.parameterName || row.parameterKey || '',
								status: finalStatus,
								unit: row.unit || '',
								value: row.value || '',
								batchNumber: isPuneJlr() ? (row.batchNumber || batchActualNumbers[i]) : batchActualNumbers[i],
								reason: skipReasonData.reason,
								remark: skipReasonData.remarks,
							});
						}
					}

					return acc;
				}, {});

				parametersSkipRequest = Object.keys(machineGroups)
					.map(machineId => ({
						machineId: parseInt(machineId),
						processesToSkip: machineGroups[machineId],
					}))
					.filter(group => group.processesToSkip.length > 0);
			}

			// Validate that we have something to skip
			if (!parametersSkipRequest || parametersSkipRequest.length === 0) {
				setIsReLoginOpen(false);
				showMessageOverlay({
					contextText: 'Skip Validation',
					info: rowsToProcess.length > 0
						? 'All batches for the selected parameter(s) are already OK'
						: 'Please select at least one torque row to skip',
					error: false,
				});
				return;
			}

			const payload = {
				partSkipRequest,
				parametersSkipRequest: (parametersSkipRequest && parametersSkipRequest.length > 0) ? parametersSkipRequest : null,
				privilegedRequest,
			};



			privilegedSkip(
				partValidation.sequenceNumber,
				payload,
				(data) => {
					setIsReLoginOpen(false);
					successCallback(data, closeSkipDialog);
				},
				(error) => {
					setIsReLoginOpen(false);
					errorCallback(error, closeSkipDialog);
				},
			);
		} catch (err) {
			setIsReLoginOpen(false);
			showMessageOverlay({
				contextText: 'Skip Error',
				info: err.message,
				error: true,
			});
		} finally {
			setLoading(false);
		}
	};


	const isMachineOp = !!(partData && partData.machineId);

	return (
		<div>
			<div
				onMouseEnter={handlePopoverOpen}
				onMouseLeave={handlePopoverClose}
			>
				<LargeButton
					name="FailForward"
					sx={{
						...btnStyles,
						fontSize: `${scaleValue(28)}px !important`,
						width: 'fit-content !important',      
						minWidth: 'fit-content !important',  
						whiteSpace: 'nowrap !important',
						padding: `${scaleValue(13)}px ${scaleValue(32)}px !important`, // ✅ padding scale ho
						fontWeight: 'bold !important',
						'&.Mui-disabled': {
							background: 'linear-gradient(90deg, #d1d1d1 0%, #7a7a79 100%) !important',
							color: '#FFFFFF !important',
							cursor: 'not-allowed !important',
							opacity: 0.7,
						},
					}}
					handleButtonClick={openSkipDialog}
					disabled={disableBtn}
				/>
			</div>

			<Popover
				open={isEllipsisPopupOpen}
				anchorEl={ellipsisAnchorEl}
				onClose={handlePopoverClose}
				className={styles.popover}
				classes={{ paper: styles.popoverPaper }}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				{hoverMessage}
			</Popover>
			<ReLogin
				open={isReLoginOpen}
				handleReLogin={handleReLogin}
				handleClose={() => setIsReLoginOpen(false)}
				isDark={themeMode === 'dark'}
			/>

			{/* Reason and Remark Modal for both PV & Torque */}
			{isSkipDialogOpen && (
				<ReasonAndRemarkModal
					nameofModal={
						skipModalTitle || (isMachineOp ? 'Skip Machine Operation' : 'Skip Part Scanning')
					}
					open={isSkipDialogOpen}
					handleClose={closeSkipDialog}
					data={{
						partName: familyName || skippedSerialNumber || 'N/A',
						partNumber: partNumber || skippedPartNumber || 'N/A',
						parentPartDescription:
							partValidation.parentPartDescription || 'N/A',
						quantity: quantity || 0,
					}}
					handleSubmit={onSkipSubmit}
					dropDown={isMachineOp ? [
						'MACHINE ERROR',
						'PARAMETER NOT OK',
						'NO RESPONSE FROM MACHINE',
						'MANUAL SKIP REQUIRED',
						'OTHER',
					] : [
						'DUPLICATE SERIAL NO.',
						'PART MISMATCHED',
						'SERIAL NO. IS BLANK',
						'DAMAGED BARCODE',
						'BARCODE NOT FOUND',
						'OTHER',
					]}
					reasonLabel={isMachineOp ? 'Reason' : 'Reason to take out the part'}
					leftTitle={isMachineOp ? 'Operation' : 'Part Name'}
					rightTitle={isMachineOp ? 'Value' : 'Part Number'}
					setSecondApiStop={setSecondApiStop}
				/>
			)}
		</div>
	);
};


export default connect(mapStateToProps, mapDispatchToProps)(SkipPart);
